import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import Spinner from "../../common/Spinner";
import logo from "../../../logo/Logo.svg";
import "./installPage.css";

/**
 * Public Diawi-style install landing page: /install/:appId/:buildId
 *
 * Intentionally UNAUTHENTICATED — anyone with the URL can install. Build
 * id is the bearer capability. The backend `/builds/:id/install-meta`
 * only returns public-safe fields.
 *
 * iOS: emits itms-services://?action=download-manifest&url=<manifest>
 * Android: direct signed S3 URL.
 */
const InstallPage = () => {
  const { buildId } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const apiBase = (process.env.REACT_APP_API_BASEURL || "").replace(/\/+$/, "");

  useEffect(() => {
    let cancelled = false;
    async function fetchMeta() {
      setLoading(true);
      setError("");
      try {
        // Deliberately do NOT pass Authorization header — endpoint is
        // public and adding a stale token from a previous session would
        // cause the server to reject it.
        const { data } = await axios.get(`${apiBase}/builds/${buildId}/install-meta`, {
          transformRequest: [(d, headers) => { delete headers.common?.Authorization; delete headers.Authorization; return d; }],
        });
        if (cancelled) return;
        if (data?.success === 1 && data?.data) {
          setMeta(data.data);
        } else {
          setError(data?.message || "Build not found");
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || "Could not load build");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchMeta();
    return () => {
      cancelled = true;
    };
  }, [buildId, apiBase]);

  const isIos = meta && String(meta.os).toLowerCase() === "ios";
  const isAndroid = meta && String(meta.os).toLowerCase() === "android";

  // Build the install URL.
  let installHref = null;
  if (meta) {
    if (isIos && meta.manifestPath) {
      // Apple wants the absolute https URL to the plist, URL-encoded.
      const manifestAbs = apiBase.replace(/\/api\/v1$/, "") + meta.manifestPath;
      installHref = `itms-services://?action=download-manifest&url=${encodeURIComponent(manifestAbs)}`;
    } else if (isAndroid && meta.downloadUrl) {
      installHref = meta.downloadUrl;
    }
  }

  return (
    <div className="ba-install-root">
      <div className="ba-install-card">
        <div className="ba-install-brand">
          <img src={logo} alt="Beta Appstore" />
        </div>

        {loading && (
          <div className="ba-install-loading">
            <Spinner size={56} />
            <div>Loading build…</div>
          </div>
        )}

        {!loading && error && (
          <div className="ba-install-error" role="alert">
            <div className="ba-install-error-title">We couldn&apos;t load this build</div>
            <div className="ba-install-error-body">{error}</div>
          </div>
        )}

        {!loading && !error && meta && (
          <>
            <div className="ba-install-app">
              {meta.iconUrl ? (
                <img
                  src={meta.iconUrl}
                  alt={`${meta.appName} icon`}
                  className="ba-install-icon"
                />
              ) : (
                <div className="ba-install-icon ba-install-icon-placeholder" aria-hidden />
              )}
              <div className="ba-install-app-text">
                <h1 className="ba-install-app-name">{meta.title || meta.appName}</h1>
                <div className="ba-install-app-sub">
                  <span className="ba-install-chip ba-install-chip-os">
                    {isIos ? "iOS" : isAndroid ? "Android" : meta.os}
                  </span>
                  {meta.envirionment && (
                    <span
                      className={`ba-install-chip ${
                        meta.envirionment === "Production"
                          ? "ba-install-chip-prod"
                          : "ba-install-chip-staging"
                      }`}
                    >
                      {meta.envirionment}
                    </span>
                  )}
                  {meta.version && (
                    <span className="ba-install-version">v{meta.version}</span>
                  )}
                </div>
                {meta.bundleId && (
                  <div className="ba-install-bundle">{meta.bundleId}</div>
                )}
              </div>
            </div>

            {meta.notes && (
              <div className="ba-install-notes">
                <div className="ba-install-notes-title">Release notes</div>
                <div
                  className="ba-install-notes-body"
                  // notes are rich text from CKEditor already — safe per product intent
                  dangerouslySetInnerHTML={{ __html: meta.notes }}
                />
              </div>
            )}

            <div className="ba-install-cta">
              {installHref ? (
                <a
                  className="ba-install-btn"
                  href={installHref}
                  {...(isAndroid ? { download: true } : {})}
                >
                  {isIos ? "Install on iPhone" : "Download APK"}
                </a>
              ) : (
                <div className="ba-install-unavailable">
                  Install isn&apos;t available for this build yet. The IPA may be
                  missing its Info.plist metadata — re-upload to fix.
                </div>
              )}
              {isIos && (
                <div className="ba-install-hint">
                  Open this page on your iPhone&apos;s Safari. iOS will prompt
                  &ldquo;Install App?&rdquo; after you tap the button.
                </div>
              )}
              {isAndroid && (
                <div className="ba-install-hint">
                  Open the downloaded APK and allow installation from this
                  source when Android prompts.
                </div>
              )}
            </div>

            {/* Scan-to-install: renders the current URL as a QR code so a
                desktop user can scan it with their phone's camera instead
                of retyping / emailing the link. Hidden on mobile (pointer
                is coarse) — the installer button above is the right path
                on touch devices. */}
            <div className="ba-install-qr">
              <div className="ba-install-qr-box">
                <QRCodeSVG
                  value={typeof window !== "undefined" ? window.location.href : ""}
                  size={128}
                  level="M"
                  includeMargin={false}
                  bgColor="#ffffff"
                  fgColor="#0F172A"
                />
              </div>
              <div className="ba-install-qr-text">
                <div className="ba-install-qr-title">Scan to install on your phone</div>
                <div className="ba-install-qr-sub">
                  Open your phone&apos;s camera and point it at this code —
                  tap the banner that appears to open this page.
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <footer className="ba-install-footer">
        <span>Beta Appstore · private distribution</span>
      </footer>
    </div>
  );
};

export default InstallPage;
